import { createFileRoute, Link, Outlet, useRouterState, redirect } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useQuizlet } from '@/queries/quizlets';
import { QuizSession } from '@/components/QuizSession';
import { CircularLoader } from '@/components/CircularLoader';
import type { Question } from '@/types';
import styles from './quizletId.module.scss';

export const Route = createFileRoute('/quizlets/$quizletId')({
  component: QuizletDetail,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function QuizletDetail() {
  const { quizletId } = Route.useParams();
  const { data: quizlet, isLoading, error } = useQuizlet(quizletId);
  const routerState = useRouterState();

  // Check if we're on a child route (like /edit)
  const isChildRoute = routerState.location.pathname.endsWith('/edit');

  if (isLoading) {
    return (
      <div className={styles.quizletDetail__loadingContainer}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.quizletDetail__errorContainer}>
        <div className={styles.quizletDetail__errorText}>Error: {error.message}</div>
      </div>
    );
  }

  if (!quizlet || !quizlet.questions || quizlet.questions.length === 0) {
    return (
      <div className={styles.quizletDetail__notFoundContainer}>
        <div className={styles.quizletDetail__notFoundText}>Quizlet not found or has no questions</div>
      </div>
    );
  }

  // If we're on a child route (like /edit), render the child route component
  if (isChildRoute) {
    return <Outlet />;
  }

  // Otherwise, render the quiz session (detail view)
  return (
    <div className={styles.quizletDetail}>
      <div className={styles.quizletDetail__header}>
        <div className={styles.quizletDetail__headerContent}>
          <div>
            <h1 className={styles.quizletDetail__title}>{quizlet.name}</h1>
            <p className={styles.quizletDetail__subtitle}>
              {quizlet.questions.length} question{quizlet.questions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link
            to="/quizlets/$quizletId/edit"
            params={{ quizletId }}
          >
            <motion.button
              className={styles.quizletDetail__editButton}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Edit Quiz
            </motion.button>
          </Link>
        </div>
      </div>

      <QuizSession
        questions={quizlet.questions as Question[]}
        title={quizlet.name}
        quizletId={quizletId}
        isDailyKnowledge={false}
      />
    </div>
  );
}
